import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Trash2, Loader2, X } from 'lucide-react';
import { isNativeApp } from '../lib/native';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };

// App Store 5.1.1(v): in-app, self-service permanent account deletion.
export const DeleteAccountCard = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/auth/account/deletion-eligibility`, { withCredentials: true })
      .then((r) => setEligibility(r.data))
      .catch(() => setEligibility({ eligible: true }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/auth/account/delete`, { password, confirm }, { withCredentials: true });
      sessionStorage.setItem('civilx_login_notice', 'Your account and personal information have been permanently deleted.');
      setUser(false);
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not delete your account. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-red-200 rounded-md p-6 mt-6" data-testid="delete-account-card">
      <h2 className="text-lg font-bold tracking-tight text-red-700 mb-2 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Trash2 className="w-5 h-5" /> Delete Account
      </h2>
      <p className="text-sm text-slate-600 mb-4" style={FONT}>
        This permanently deletes your CivilX user account and associated personal information. Information CivilX is
        legally required to retain (for example payroll and settlement records your company must keep) may be retained
        as required by law. See our{' '}
        <a href="/privacy-policy" target={isNativeApp() ? undefined : "_blank"} rel="noreferrer" className="text-[#002FA7] underline" data-testid="delete-account-privacy-link">Privacy Policy</a>.
      </p>
      {eligibility && !eligibility.eligible && (
        <div className="mb-4 flex gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3" style={FONT} data-testid="delete-account-blocked">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{eligibility.reason}</span>
        </div>
      )}
      <button
        type="button"
        data-testid="delete-account-button"
        onClick={() => setOpen(true)}
        disabled={!eligibility || !eligibility.eligible}
        className="px-4 py-2 rounded-md border border-red-300 text-red-700 font-semibold text-sm hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
        style={FONT}
      >
        Delete my account…
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
          <form onSubmit={submit} className="bg-white w-full max-w-md rounded-lg shadow-xl p-6 space-y-4" data-testid="delete-account-modal" style={FONT}>
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Permanently delete your account?</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-slate-500" aria-label="Close" data-testid="delete-account-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600">This cannot be undone. You will be signed out immediately and will no longer be able to log in.</p>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600">
              Your password
              <input data-testid="delete-account-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm normal-case tracking-normal focus:outline-none focus:border-red-500" />
            </label>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600">
              Type <span className="text-red-700">DELETE</span> to confirm
              <input data-testid="delete-account-confirm" type="text" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoCapitalize="characters" autoComplete="off"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm tracking-normal focus:outline-none focus:border-red-500" />
            </label>
            {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="delete-account-error">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="delete-account-cancel">Cancel</button>
              <button type="submit" disabled={busy || confirm.trim().toUpperCase() !== 'DELETE' || !password}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="delete-account-submit">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Permanently delete
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountCard;
